<template>
  <div class="vx-col" id="coupon-list">
    <vs-alert class="mb-3" color="danger" icon="new_releases" v-show="apiErrors.length">
      <small>{{ apiErrors[0] }}</small>
    </vs-alert>

    <div class="mb-base w-full" v-if="isAvailable">
      <h5 class="mb-3">{{ capitalize($t("coupon.available")) }}</h5>
      <vs-divider></vs-divider>
      <div class="vx-row" v-if="countRecordIf(couponList, 'is_valid') > 0">
        <template v-for="(coupon, index) in couponList">
          <div class="vx-col w-full lg:w-1/2 mb-2" :key="`is_available_coupons_${index}`" v-if="coupon.is_valid">
            <vx-card>
              <table class="w-full">
                <tr>
                  <td width="30%" class="text-center">
                    <vs-avatar size="large" color="success" icon-pack="feather" icon="icon-tag" />
                  </td>
                  <td>
                    <div class="font-bold text-xl">
                      <i18n path="coupon.offAmount" tag="label">
                        {{ renderCurrency(coupon.amount, coupon.country.currency) }}
                      </i18n>
                    </div>
                    <div>{{ $t("coupon.anyOrder") }}</div>
                    <div>
                      <small>{{ $t("coupon.expiredAt") }} {{ renderDateFormat(coupon.expired_date) }}</small>
                    </div>
                  </td>
                </tr>
              </table>

              <template slot="footer" v-if="isOnClickApplyCoupon">
                <div class="flex justify-between flex-wrap">
                  <span>&nbsp;</span>
                  <vs-button size="small" type="filled" @click="onClickApplyCoupon(coupon)">
                    {{ $t("action.apply") }}
                  </vs-button>
                </div>
              </template>
            </vx-card>
          </div>
        </template>
      </div>
      <div class="text-center" v-else>
        <i>--- {{ $t("general.notAvailable", { msg: $t("attribute.coupon") }) }} ---</i>
      </div>
    </div>

    <div class="mb-base w-full" v-if="isApplied">
      <h5 class="mb-3">{{ capitalize($t("coupon.used")) }}</h5>
      <vs-divider></vs-divider>
      <div class="vx-row" v-if="countRecordIf(couponList, 'is_applied') > 0">
        <template v-for="(coupon, index) in couponList">
          <div class="vx-col w-full lg:w-1/2 mb-2" :key="`is_applied_coupons_${index}`" v-if="coupon.is_applied">
            <vx-card>
              <table class="w-full">
                <tr>
                  <td width="30%" class="text-center">
                    <vs-avatar size="large" color="smoke" icon-pack="feather" icon="icon-tag" />
                  </td>
                  <td>
                    <div class="font-bold text-xl">
                      <i18n path="coupon.offAmount" tag="label">
                        {{ renderCurrency(coupon.amount, coupon.country.currency) }}
                      </i18n>
                    </div>
                    <div class="mb-2">{{ $t("coupon.anyOrder") }}</div>
                    <div>
                      <small>{{ $t("coupon.expiredAt") }} {{ renderDateFormat(coupon.expired_date) }}</small>
                    </div>
                  </td>
                </tr>
              </table>
            </vx-card>
          </div>
        </template>
      </div>
      <div class="text-center" v-else>
        <i>--- {{ $t("general.notAvailable", { msg: $t("attribute.coupon") }) }} ---</i>
      </div>
    </div>

    <div class="mb-base w-full" v-if="isExpired">
      <h5 class="mb-3">{{ capitalize($t("coupon.expired")) }}</h5>
      <vs-divider></vs-divider>
      <div class="vx-row" v-if="countRecordIf(couponList, 'is_expired') > 0">
        <template v-for="(coupon, index) in couponList">
          <div class="vx-col w-full lg:w-1/2 mb-2" :key="`is_expired_coupons_${index}`" v-if="coupon.is_expired">
            <vx-card>
              <table class="w-full">
                <tr>
                  <td width="30%" class="text-center">
                    <vs-avatar size="large" color="danger" icon-pack="feather" icon="icon-tag" />
                  </td>
                  <td>
                    <div class="font-bold text-xl">
                      <i18n path="coupon.offAmount" tag="label">
                        {{ renderCurrency(coupon.amount, coupon.country.currency) }}
                      </i18n>
                    </div>
                    <div class="mb-2">{{ $t("coupon.anyOrder") }}</div>
                    <div>
                      <small>{{ $t("coupon.expiredAt") }} {{ renderDateFormat(coupon.expired_date) }}</small>
                    </div>
                  </td>
                </tr>
              </table>
            </vx-card>
          </div>
        </template>
      </div>
      <div class="text-center" v-else>
        <i>--- {{ $t("general.notAvailable", { msg: $t("attribute.coupon") }) }} ---</i>
      </div>
    </div>

    <vs-pagination
      v-model="pagination.current_page"
      :total="pagination.last_page"
      :max="6"
      @input="(value) => onPaginateTransaction(value)"
    >
    </vs-pagination>
  </div>
</template>

<script>
import Global from "@/helper/global";
import ErrorHandler from "@/helper/error_handler";

export default {
  name: "coupon-list",
  props: {
    isOnClickApplyCoupon: {
      type: Boolean,
      default: false,
    },
    isAvailable: {
      type: Boolean,
      default: true,
    },
    isApplied: {
      type: Boolean,
      default: false,
    },
    isExpired: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      couponList: [],
      apiErrors: [],
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 0,
      },
    };
  },
  mounted() {
    this.retrieveCouponList();
  },
  methods: {
    retrieveCouponList() {
      this.$vs.loading({
        container: "#coupon-list",
        scale: 0.6,
      });

      this.apiErrors = [];

      let param = {
        paginate_count: this.pagination.per_page,
        page: this.pagination.current_page,
      };

      this.$http
        .get(this.$endpoints.customer_coupon, {
          params: param,
        })
        .then((res) => {
          let response = res.data;
          this.couponList = response.data;
          this.pagination = response.meta;
        })
        .catch((err) => {
          this.apiErrors = ErrorHandler.processErrorMessage(err);
        })
        .finally(() => {
          this.$vs.loading.close("#coupon-list > .con-vs-loading");
        });
    },
    onPaginateTransaction(page) {
      this.retrieveCouponList();
    },
    countRecordIf(arr, key) {
      return Global.countRecordIf(arr, key);
    },
    onClickApplyCoupon(coupon) {
      this.$emit("onSelectCoupon", coupon);
    },
    renderDateFormat(datetime) {
      return Global.renderDateByFormat(datetime, "YYYY-MM-DD", "DD MMM YYYY");
    },
  },
};
</script>
